// Modelo tipado del estado serializable de la pizarra (KonvaBoard).
// Se guarda dentro de `graphics[].json` en las entidades Play/Drill.
// Los campos `id`, `sourceId` y `targetId` habilitan la futura línea de
// tiempo y animación: identifican elementos de forma estable entre sesiones.

import { ArrowKind } from '../components/konva-board/konva-board.types';
import { CourtMode } from '../utils/court';

/** Elemento posicionable del tablero (jugadores, defensores, iconos, etc.). */
export interface BoardElement {
  /** Identificador estable del elemento (persistente entre sesiones). */
  id: string;
  x: number;
  y: number;
  color?: string;
  rotation?: number;
}

/** Jugador ofensivo (círculo numerado). */
export interface PlayerElement extends BoardElement {
  number: number;
  color: string;
}

/** Defensor (círculo relleno numerado). */
export interface DefenderElement extends BoardElement {
  number: number;
  color: string;
}

/** Entrenador (icono con la letra C). */
export interface CoachElement extends BoardElement {
  color: string;
}

/** Balón (icono). */
export interface BallElement extends BoardElement {}

/** Cono (icono). */
export interface ConeElement extends BoardElement {}

/** Handoff / entrega (icono). */
export interface HandoffElement extends BoardElement {}

/** Texto libre sobre la cancha. */
export interface TextElement extends BoardElement {
  text: string;
  color: string;
}

/**
 * Flecha con trayectoria.
 * `sourceId`/`targetId` enlazan la flecha con los elementos que conecta
 * (p. ej. una pass-arrow entre dos jugadores) y son la base de la animación.
 */
export interface BoardArrow {
  id: string;
  type: ArrowKind;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cx?: number;
  cy?: number;
  points?: number[];
  color: string;
  sourceId?: string | null;
  targetId?: string | null;
  /** Orden de dibujo en la capa (0 = la primera). Base para ordenar la línea de tiempo por defecto. */
  order?: number;
  /** Si es true, la flecha es una indicación orientativa: no genera eventos de
   *  animación (ni pases, ni movimientos, ni botes) y solo se muestra estática
   *  durante el intervalo de su evento de tipo 'indication' en la línea de tiempo. */
  indication?: boolean;
}

/** Tipos de eventos soportados por la línea de tiempo de la animación. */
export type TimelineEventType = 'pass' | 'dribble' | 'move' | 'block' | 'shot' | 'hold' | 'role' | 'indication';

/**
 * Evento de la línea de tiempo: qué ocurre, cuándo arranca y cuánto dura.
 * La trayectoria la define la flecha referenciada por `arrowId`
 * (no se usa en eventos de tipo 'hold').
 */
export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  arrowId?: string;
  startTime: number;
  duration: number;
  sourceId?: string | null;
  targetId?: string | null;
  /** Balón que se mueve en pases/tiros/botes; si se omite y hay un único balón, se usa ese. */
  ballId?: string | null;
  /** Si es true, el evento arranca a la vez que el anterior (acción simultánea). */
  parallel?: boolean;
  /** Elemento (jugador o defensor) cuyo rol cambia en un evento de tipo 'role'. */
  subjectId?: string | null;
  /** Rol destino de un evento de tipo 'role' ('player' o 'defender'). */
  toRole?: 'player' | 'defender';
}

/** Línea de tiempo completa de la animación de un gráfico. */
export interface BoardTimeline {
  duration: number;
  events: TimelineEvent[];
}

/**
 * Rotación de los jugadores/defensores de un gráfico en una concatenación:
 * en el instante `time` (inicio del gráfico) los elementos pasan a tener el
 * ángulo de ese gráfico. Los ángulos van en radianes, indexados por id.
 */
export interface RotationKeyframe {
  /** Instante en la línea de tiempo concatenada en el que se aplica esta rotación. */
  time: number;
  /** Ángulo (radianes) por id de jugador. */
  playerAngles: Record<string, number>;
  /** Ángulo (radianes) por id de defensor. */
  defenderAngles: Record<string, number>;
}

/**
 * Visibilidad de los elementos de un gráfico en una concatenación: en el
 * instante `time` (inicio del gráfico) solo se muestran los elementos cuyos
 * ids están en `visibleIds` (jugadores, defensores, balones, conos, etc.
 * presentes en ese gráfico). Así los elementos añadidos o borrados entre
 * gráficos solo aparecen en la parte de la animación de su gráfico.
 */
export interface ElementVisibilityKeyframe {
  /** Instante en la línea de tiempo concatenada en el que se aplica esta visibilidad. */
  time: number;
  /** Ids de los elementos visibles desde este instante. */
  visibleIds: string[];
}

/**
 * Posiciones iniciales de los elementos animables de un gráfico en una
 * concatenación: en el instante `time` (inicio del gráfico) los jugadores,
 * defensores y balones pasan a la posición guardada de ese gráfico. Así cada
 * gráfico reproduce su propia animación completa, una detrás de otra, sin
 * mezclar los estados de los gráficos.
 */
export interface PositionKeyframe {
  /** Instante en la línea de tiempo concatenada en el que se aplican estas posiciones. */
  time: number;
  /** Posición por id de elemento (jugadores, defensores y balones). */
  positions: Record<string, { x: number; y: number }>;
}

/**
 * Estado completo serializable de la pizarra.
 * Compatible hacia atrás: los gráficos antiguos sin `id`/`timeline` siguen cargando.
 * `firstGraphicArrows`, `rotationKeyframes`, `elementVisibility` y
 * `positionKeyframes` son metadata exclusiva de las concatenaciones
 * (concatGraphics): la primera guarda los ids de las flechas del primer
 * gráfico (para que al volver al 0.0s tras la reproducción solo se muestre ese
 * gráfico), la segunda las rotaciones por gráfico (para que al llegar a la
 * parte de cada gráfico se aplique su rotación), la tercera los elementos
 * visibles por gráfico (para que solo se muestre lo que tiene cada gráfico en
 * su momento) y la cuarta las posiciones por gráfico (para que cada gráfico
 * reproduzca su propia animación una detrás de otra). Los jsons guardados en
 * BD no los incluyen.
 */
export interface BoardJson {
  players: PlayerElement[];
  playerAngles: number[];
  defenders: DefenderElement[];
  defenderAngles: number[];
  coaches: CoachElement[];
  balls: BallElement[];
  cones: ConeElement[];
  handoffs: HandoffElement[];
  texts: TextElement[];
  arrows: BoardArrow[];
  curvedArrows: BoardArrow[];
  blockArrows: BoardArrow[];
  curvedBlockArrows: BoardArrow[];
  passArrows: BoardArrow[];
  shotArrows: BoardArrow[];
  dribbleArrows: BoardArrow[];
  curvedDribbleArrows: BoardArrow[];
  courtMode: CourtMode;
  timeline?: BoardTimeline;
  firstGraphicArrows?: string[];
  /** Rotaciones por gráfico en una concatenación (ver RotationKeyframe). */
  rotationKeyframes?: RotationKeyframe[];
  /** Elementos visibles por gráfico en una concatenación (ver ElementVisibilityKeyframe). */
  elementVisibility?: ElementVisibilityKeyframe[];
  /** Posiciones por gráfico en una concatenación (ver PositionKeyframe). */
  positionKeyframes?: PositionKeyframe[];
}

/**
 * Claves de las listas de flechas del BoardJson, en el orden canónico de
 * dibujo. Fuente única de verdad: la concatenación, la animación, la edición
 * de la línea de tiempo y el playback recorren aquí todas las flechas sin
 * repetir la lista en cada sitio.
 */
export const ARROW_KEYS: (keyof BoardJson)[] = ['arrows', 'curvedArrows', 'blockArrows', 'curvedBlockArrows', 'passArrows', 'shotArrows', 'dribbleArrows', 'curvedDribbleArrows'];

/** Flecha de movimiento simplificada (solo extremos), usada al "continuar editando". */
export interface MovementArrow {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
